import type { QueryClient } from 'react-query'
import { toast } from 'react-toastify'

import type { UseBenchmarkingSetupResult } from 'features/trend-detection/features/agent-create/hooks/use-benchmarking-setup'
import {
  ResearchAgentsAction,
  trackResearchAgents,
} from 'features/trend-detection/mixpanel'
import type { TrendDetectionService } from 'features/trend-detection/service'
import { buildBenchmarkingFiltersV2 } from 'features/trend-detection/utils/agent-filters-v2'
import { invalidateFeedQueries } from 'features/trend-detection/utils/invalidate-feed-queries'

/** PATCHes the agent's config + email preference, for
 * `useBenchmarkingUpdateSubmit`. Returns whether it succeeded, toasting the
 * failure itself so the caller only branches on the boolean. */
export async function updateAgentConfig(
  service: TrendDetectionService,
  projectId: string,
  agentId: string,
  benchmarkingSetup: UseBenchmarkingSetupResult,
): Promise<boolean> {
  try {
    await service.updateBenchmarkingAgent(projectId, agentId, {
      name: benchmarkingSetup.agentName,
      ...buildBenchmarkingFiltersV2(
        benchmarkingSetup.cohortFilters,
        benchmarkingSetup.focusGroupFilters,
      ),
    })
    await service.setBenchmarkingAgentEmailPreference(
      projectId,
      agentId,
      benchmarkingSetup.emailNotifications,
    )
    return true
  } catch (err) {
    toast.error(
      err instanceof Error
        ? err.message
        : 'Failed to update benchmarking agent',
    )
    return false
  }
}

/** Runs the agent and invalidates the feed/list/detail caches, for
 * `useBenchmarkingUpdateSubmit`. Same toast-on-failure contract as
 * `updateAgentConfig`. */
export async function runAndInvalidate(
  service: TrendDetectionService,
  queryClient: QueryClient,
  projectId: string,
  agentId: string,
  agentName: string,
): Promise<boolean> {
  try {
    await service.runBenchmarkingAgent(projectId, agentId)
    trackResearchAgents(ResearchAgentsAction.agent_run, {
      agent_type: 'benchmarking',
      agent_uuid: agentId,
      agent_name: agentName,
      trigger: 'confirm_and_run',
    })
    await invalidateFeedQueries(queryClient, projectId)
    queryClient.invalidateQueries(['benchmarking-agents', projectId])
    queryClient.invalidateQueries(['benchmarking-agent', projectId, agentId])
    toast.success('Agent saved; run started.')
    return true
  } catch (err) {
    toast.error(
      err instanceof Error ? err.message : 'Failed to start benchmarking run',
    )
    return false
  }
}
